import { useCallback, useEffect, useRef } from 'react';
import { useApp } from '@context/AppContext';
import { useFirebase } from '@/App';
import { shuffleTurnOrder, getNextTurn } from '@utils/turnManager';

/**
 * Hook providing all Relay Mode game actions.
 * Turn management is host-driven — the host client advances turns on timer expiry.
 * Host auto-promotion is handled automatically: when isHost flips true,
 * the useEffect re-evaluates and picks up expired timers.
 */
export function useRelay() {
  const { state, dispatch } = useApp();
  const firebase = useFirebase();
  const { user, room, game } = state;
  const advancingRef = useRef(false);
  const lastReactionRef = useRef(0);

  // Host: initialise relay with settings
  const initRelay = useCallback(async ({ timerDuration, totalRounds, theme, blindRelay, randomOrder }) => {
    const memberIds = Object.keys(room.members || {});
    const turnOrder = randomOrder !== false ? shuffleTurnOrder(memberIds) : memberIds;

    await firebase.updateGameState({
      turnOrder,
      totalRounds: totalRounds || 1,
      timerDuration: timerDuration || 20,
      theme: theme || null,
      blindRelay: blindRelay || false,
      currentDrawerId: null,
      turnHistory: null,
      reactions: null,
    });
  }, [firebase, room.members]);

  // Host: start the relay
  const startRelay = useCallback(async () => {
    if (!game.turnOrder || game.turnOrder.length === 0) return;
    await firebase.updateGameState({
      phase: 'active',
      round: 1,
      currentDrawerId: game.turnOrder[0],
      timerEndsAt: Date.now() + (game.timerDuration * 1000),
    });
  }, [firebase, game.turnOrder, game.timerDuration]);

  // Host: advance to next turn
  const advanceTurn = useCallback(async () => {
    if (advancingRef.current) return;
    advancingRef.current = true;

    try {
      const { nextDrawerId, newRound, isGameOver } = getNextTurn(
        game.turnOrder, game.currentDrawerId, game.round, game.totalRounds
      );

      // Record turn in history
      await firebase.updateGameState({
        [`turnHistory/${Date.now()}`]: {
          userId: game.currentDrawerId,
          nickname: room.members?.[game.currentDrawerId]?.nickname || 'Unknown',
          turnIndex: game.turnOrder.indexOf(game.currentDrawerId) + ((game.round - 1) * game.turnOrder.length),
          startedAt: (game.timerEndsAt || Date.now()) - (game.timerDuration * 1000),
          endedAt: Date.now(),
        },
      });

      // Clear reactions between turns
      await firebase.clearReactions();

      if (isGameOver) {
        await firebase.updateGameState({
          phase: 'finished',
          timerEndsAt: null,
          currentDrawerId: null,
        });
      } else {
        // Transition phase (3 seconds)
        await firebase.updateGameState({
          phase: 'transition',
          currentDrawerId: nextDrawerId,
          round: newRound,
          timerEndsAt: null,
        });
      }
    } finally {
      advancingRef.current = false;
    }
  }, [firebase, game, room.members]);

  // Host: resume from transition to active
  const resumeFromTransition = useCallback(async () => {
    await firebase.updateGameState({
      phase: 'active',
      timerEndsAt: Date.now() + (game.timerDuration * 1000),
    });
  }, [firebase, game.timerDuration]);

  // Host-driven turn timer — active phase
  useEffect(() => {
    if (!user.isHost || game.phase !== 'active' || !game.timerEndsAt) return;
    const remaining = game.timerEndsAt - Date.now();
    if (remaining <= 0) {
      advanceTurn();
      return;
    }
    const timeout = setTimeout(advanceTurn, remaining);
    return () => clearTimeout(timeout);
  }, [user.isHost, game.phase, game.timerEndsAt, advanceTurn]);

  // Host-driven transition timer — resume after 3 seconds
  useEffect(() => {
    if (!user.isHost || game.phase !== 'transition') return;
    const timeout = setTimeout(resumeFromTransition, 3000);
    return () => clearTimeout(timeout);
  }, [user.isHost, game.phase, resumeFromTransition]);

  // Player: send a reaction (throttled to 2s)
  const sendReaction = useCallback(async (emoji) => {
    const now = Date.now();
    if (now - lastReactionRef.current < 2000) return;
    lastReactionRef.current = now;
    await firebase.pushReaction(emoji, user.id);
  }, [firebase, user.id]);

  // Host: play again with fresh canvas
  const playAgain = useCallback(async () => {
    // Clear canvas strokes
    await firebase.clearAllStrokes();
    dispatch({ type: 'CLEAR_ALL_STROKES' });

    // Re-shuffle and reset
    const memberIds = Object.keys(room.members || {});
    const turnOrder = shuffleTurnOrder(memberIds);
    await firebase.updateGameState({
      phase: 'lobby',
      round: 1,
      turnOrder,
      currentDrawerId: null,
      timerEndsAt: null,
      turnHistory: null,
      reactions: null,
    });
  }, [firebase, dispatch, room.members]);

  // Host: end game entirely
  const endGame = useCallback(async () => {
    await firebase.clearGame();
    dispatch({ type: 'CLEAR_GAME' });
    dispatch({ type: 'ADD_TOAST', payload: { message: 'Game ended — back to free draw' } });
  }, [firebase, dispatch]);

  return {
    initRelay,
    startRelay,
    advanceTurn,
    sendReaction,
    playAgain,
    endGame,
  };
}
